<template>
  <el-table
    :data="events"
    stripe
  >
    <el-table-column
      v-if="columns.includes('peek')"
      type="expand"
    >
      <template v-slot="{ row }">
        <slot
          name="peek"
          :event="row"
        >
          <Peek :event="row" />
        </slot>
      </template>
    </el-table-column>

    <el-table-column
      v-if="columns.includes('account')"
      label="Account"
      show-overflow-tooltip
    >
      <template v-slot="{ row }">
        <RouterLink :to="{ name: (row.account.status === 'lead' ? 'sales-opportunities-account' : 'sales-accounts-account'), params: { accountId: row.account.id } }">
          {{ row.account.name }}
        </RouterLink>
      </template>
    </el-table-column>

    <el-table-column
      v-if="columns.includes('account')"
      align="left"
      :min-width="80"
    >
      <template v-slot="{ row }">
        <el-tag
          size="mini"
          type="info"
        >
          <template v-if="row.account.status === 'lead' && row.account.lead">
            {{ $_.get(row, 'account.lead.status', '-') | titleize }}
          </template>

          <template v-else>
            {{ $_.get(row, 'account.status', '-') | titleize }}
          </template>
        </el-tag>
      </template>
    </el-table-column>

    <el-table-column
      v-if="columns.includes('task')"
      prop="type"
      label="Task"
      show-overflow-tooltip
      :formatter="(row, column, cellValue) => $_.get(cellValue, 'name', '-')"
    />

    <el-table-column
      v-if="columns.includes('due')"
      prop="due_at"
      label="Due"
      align="center"
      :min-width="80"
      show-overflow-tooltip
      :formatter="(row, column, cellValue) => cellValue ? $moment(cellValue).calendar(null, {
        sameDay: '[Today at] hh:mm a',
        nextDay: '[Tomorrow,] hh:mm a',
        lastDay: '[Yesterday at] hh:mm a',
        nextWeek: 'DD/MM/YYYY[,] hh:mm a',
        lastWeek: 'DD/MM/YYYY[,] hh:mm a',
        sameElse: 'DD/MM/YYYY[,] hh:mm a'
      }) : '-'"
    />

    <el-table-column
      v-if="columns.includes('priority')"
      prop="priority"
      label="Priority"
      align="center"
      :min-width="80"
    >
      <template v-slot="{ row }">
        <el-tag
          :type="getTagByPriority(row.priority)"
          size="mini"
        >
          {{ row.priority | titleize }}
        </el-tag>
      </template>
    </el-table-column>

    <el-table-column
      v-if="columns.includes('actions')"
      label="Actions"
      align="right"
      :min-width="120"
    >
      <template v-slot="{ row }">
        <slot
          name="actions"
          :event="row"
        >
          <template v-if="row.completed_at">
            <span class="text-muted font-italic">Completed {{ row.completed_at | moment('from', 'now') }}</span>
          </template>

          <template v-else>
            <el-button
              size="mini"
              type="primary"
              @click.native="completeEvent(row)"
            >
              Complete
            </el-button>

            <el-button
              size="mini"
              @click.native="deferEvent(row)"
            >
              Defer
            </el-button>
          </template>
        </slot>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import Peek from '@/components/Application/Sales/Events/Event/Peek'
import CreateFormDialog from '@/components/Application/Sales/Events/Event/CreateDialog'
import CompleteFormDialog from '@/components/Application/Sales/Events/Event/CompleteDialog'
import DeferFormDialog from '@/components/Application/Sales/Events/Event/DeferDialog'
import Vue from 'vue'

export default {
  components: {
    Peek
  },
  props: {
    events: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: false,
      default: () => ([
        'peek',
        'account',
        'task',
        'due',
        'priority',
        'actions'
      ])
    }
  },
  data () {
    return {
      completeFormDialogVisible: false,
      deferFormDialogVisible: false,
      createFormDialogVisible: false,
      completeFormDialogInstance: undefined,
      deferFormDialogInstance: undefined,
      createFormDialogInstance: undefined
    }
  },
  watch: {
    completeFormDialogVisible (visible) {
      if (!visible && typeof this.completeFormDialogInstance !== 'undefined') {
        this.completeFormDialogInstance.$destroy()
        this.completeFormDialogInstance.$el.remove()
        this.completeFormDialogInstance = undefined
      }
    },
    deferFormDialogVisible (visible) {
      if (!visible && typeof this.deferFormDialogInstance !== 'undefined') {
        this.deferFormDialogInstance.$destroy()
        this.deferFormDialogInstance.$el.remove()
        this.deferFormDialogInstance = undefined
      }
    },
    createFormDialogVisible (visible) {
      if (!visible && typeof this.createFormDialogInstance !== 'undefined') {
        this.createFormDialogInstance.$destroy()
        this.createFormDialogInstance.$el.remove()
        this.createFormDialogInstance = undefined
      }
    }
  },
  methods: {
    getTagByPriority (priority) {
      switch (priority) {
        case 'high':
          return 'danger'
        case 'medium':
          return 'warning'
        case 'low':
          return 'success'
        case 'normal':
        default:
          return 'primary'
      }
    },
    completeEvent (event) {
      const CompleteFormDialogClass = Vue.extend(CompleteFormDialog)

      this.completeFormDialogInstance = new CompleteFormDialogClass({
        propsData: {
          event: event.clone()
        }
      })

      this
        .completeFormDialogInstance
        .$on('destroy', () => {
          this.completeFormDialogVisible = false
        })

      this
        .completeFormDialogInstance
        .$on('new-event', (newEvent) => {
          this.createEvent(newEvent)
        })

      this
        .completeFormDialogInstance
        .$on('completed-event', (event) => {
          this.$emit('completed-event', event)
        })

      this
        .completeFormDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.completeFormDialogInstance.$el)
    },
    deferEvent (event) {
      const DeferFormDialogClass = Vue.extend(DeferFormDialog)

      this.deferFormDialogInstance = new DeferFormDialogClass({
        propsData: {
          event: event.clone()
        }
      })

      this
        .deferFormDialogInstance
        .$on('destroy', () => {
          this.deferFormDialogVisible = false
        })

      this
        .deferFormDialogInstance
        .$on('deferred-event', (event) => {
          this.$emit('deferred-event', event)
        })

      this
        .deferFormDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.deferFormDialogInstance.$el)
    },
    createEvent (event) {
      const CreateFormDialogClass = Vue.extend(CreateFormDialog)

      this.createFormDialogInstance = new CreateFormDialogClass({
        propsData: {
          event: event,
          cancellable: false,
          disabledFields: [
            (event.account ? 'account' : undefined),
            (event.type ? 'type' : undefined)
          ]
        }
      })

      this
        .createFormDialogInstance
        .$on('destroy', () => {
          this.createFormDialogVisible = false
        })

      this
        .createFormDialogInstance
        .$on('created-event', (event) => {
          this.$emit('created-event', event)
        })

      this
        .createFormDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.createFormDialogInstance.$el)
    }
  }
}
</script>
